import React, { useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import InputField from "components/fields/InputField";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";

const InputStyle =
  "daterange-picker font-medium text-navy-700 h-10 mt-2 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)", // Default border color
      borderRadius: "2px",
      _hover: {
        background: "gray", // Background color on hover
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray", // Background color for dates in the selected range
      color: "white", // Text color for dates in the selected range
    },
    selectedBtnProps: {
      background: "black", // Background color for selected date
      color: "white", // Text color for selected date
    },
    todayBtnProps: {
      // color: "#422afb", // Background color for today's date
      // fontWeight: "bold",
    },
  },
  inputProps: {
    // minWidth: "230px",
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)", // Apply custom box shadow
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9", // Border color for the calendar wrapper
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

const ActivityLogSearch: React.FC<{
  showFilters: boolean;
  isLoading: boolean;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  setSelectedDates: any;
  selectedDates: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
}> = ({
  showFilters,
  isLoading,
  filters,
  setFilters,
  setSelectedDates,
  selectedDates,
  handleSearchSubmit,
  handleSearchClear,
  isFilterLoading,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // Modal states for merchant and subaccount selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterActivityLogs", merchantData.id);
    localStorage.setItem("subaccountIdToFilterActivityLogs", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterActivityLogs", "");
    localStorage.setItem("subaccountIdToFilterActivityLogs", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    localStorage.setItem("subaccountIdToFilterActivityLogs", subaccountData.id);
    setFilters({ ...filters, subaccount_id: subaccountData.id });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    localStorage.setItem("subaccountIdToFilterActivityLogs", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  const handleClear = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterActivityLogs", "");
    localStorage.setItem("subaccountIdToFilterActivityLogs", "");
    handleSearchClear();
  };
  if (!showFilters) return null;
  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="tracking-tight text-gray-900 md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Date
          </label>
          <div className="date-picker-custom">
            <RangeDatepicker
              disabled={isFilterLoading}
              selectedDates={selectedDates}
              onDateChange={setSelectedDates}
              propsConfigs={dateRangePropsConfigs}
              configs={{
                dateFormat: "MMM d, yyyy",
              }}
              maxDate={new Date()}
            />
          </div>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Merchant
          </label>
          <Tooltip
            title={selectedMerchant ? selectedMerchant.name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !isFilterLoading && setIsMerchantModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isFilterLoading
                  ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                  : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedMerchant ? selectedMerchant.name : "All"}
              </span>
              {selectedMerchant && !isFilterLoading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Subaccount
          </label>
          <Tooltip
            title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !isFilterLoading && selectedMerchant && setIsSubaccountModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isFilterLoading || !selectedMerchant
                  ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                  : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
              </span>
              {selectedSubaccount && !isFilterLoading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Role
          </label>
          <Select
            isDisabled={isLoading}
            id="role"
            value={filters.role}
            onChange={handleInputChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="superadmin">superadmin</option>
            <option value="admin">admin</option>
            <option value="merchant">merchant</option>
            <option value="user">user</option>
          </Select>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Username
          </label>
          <InputField
            disabled={isLoading}
            variant="auth"
            extra="shadow-sm"
            label=""
            placeholder=""
            id="username"
            type="text"
            autoComplete="off"
            value={filters.username}
            onChange={handleInputChange}
          />
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Action
          </label>
          <Select
            isDisabled={isLoading}
            id="action"
            value={filters.action}
            onChange={handleInputChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="agent">agent</option>
            <option value="batch">batch</option>
            <option value="login">login</option>
            <option value="merchant">merchant</option>
            <option value="payout">payout</option>
            <option value="provider">provider</option>
            <option value="receiver">receiver</option>
            <option value="receiver account">receiver account</option>
            <option value="subaccount">subaccount</option>
            <option value="trade">trade</option>
            <option value="transaction">transaction</option>
            <option value="user">user</option>
            <option value="whitelist address">whitelist address</option>
          </Select>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Details
          </label>
          <InputField
            disabled={isLoading}
            variant="auth"
            extra="shadow-sm"
            label=""
            placeholder=""
            id="details"
            type="text"
            autoComplete="off"
            value={filters.details}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-1 items-end justify-end gap-2">
        <Button
          isDisabled={isLoading}
          onClick={() => handleClear()}
          className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
        >
          Clear
        </Button>

        <Button
          isDisabled={isLoading}
          onClick={() => handleSearchSubmit()}
          className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="relative z-10 tracking-tight">Search Logs</span>
          <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
        </Button>
      </div>

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && selectedMerchant && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant.id}
        />
      )}
    </Box>
  );
};

export default ActivityLogSearch;
